import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const SERVICE_LINKS = [
  { path: '/perimenopause', label: 'Perimenopause' },
  { path: '/menopause', label: 'Menopause' },
  { path: '/pmos', label: 'PMOS (formerly PCOS)' },
  { path: '/pmdd', label: 'PMDD' },
  { path: '/postpartum', label: 'Postpartum' },
  { path: '/thyroid', label: 'Thyroid' },
];

const Navigation = () => {
  const location = useLocation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // Close on outside click (helps after click-to-open)
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on route change
  useEffect(() => {
    setServicesOpen(false);
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setServicesOpen(true);
  };

  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setServicesOpen(false), 150);
  };

  const isServicesActive =
    location.pathname === '/services' ||
    SERVICE_LINKS.some((s) => s.path === location.pathname);

  return (
    <nav className="navigation-header">
      <Link to="/" className="navigation-logo">
        <div className="logo-container">
          <img
            src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
            alt="Glow Health Logo"
            className="logo-symbol"
          />
          <span className="logo-text">Glow Health</span>
        </div>
      </Link>

      <ul className="navigation-menu">
        <li>
          <Link
            to="/"
            className={`navigation-link ${location.pathname === '/' ? 'active' : ''}`}
          >
            Home
          </Link>
        </li>

        {/* Services dropdown */}
        <li
          className="navigation-dropdown"
          ref={dropdownRef}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <button
            type="button"
            className={`navigation-link navigation-dropdown-trigger ${isServicesActive ? 'active' : ''}`}
            aria-expanded={servicesOpen}
            aria-haspopup="true"
            onClick={() => setServicesOpen((v) => !v)}
            data-testid="nav-services-trigger"
          >
            Services
            <ChevronDown size={14} className={`navigation-dropdown-chev ${servicesOpen ? 'open' : ''}`} />
          </button>
          {servicesOpen && (
            <div className="navigation-dropdown-menu" data-testid="nav-services-menu">
              <Link to="/services" className="navigation-dropdown-all">
                View all services
              </Link>
              <ul>
                {SERVICE_LINKS.map((s) => (
                  <li key={s.path}>
                    <Link
                      to={s.path}
                      className={location.pathname === s.path ? 'active' : ''}
                      data-testid={`nav-service-${s.label.toLowerCase()}`}
                    >
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>

        <li>
          <Link
            to="/pricing"
            className={`navigation-link ${location.pathname === '/pricing' ? 'active' : ''}`}
          >
            Pricing
          </Link>
        </li>
        <li>
          <Link
            to="/assessment"
            className={`navigation-link ${location.pathname === '/assessment' ? 'active' : ''}`}
          >
            Assessment
          </Link>
        </li>
        <li>
          <Link
            to="/mystory"
            className={`navigation-link ${location.pathname === '/mystory' ? 'active' : ''}`}
          >
            My Story
          </Link>
        </li>
      </ul>

      <div className="navigation-utilities">
        <div className="nav-cta-wrapper">
          <a
            href="/book"
            className="btn-primary nav-cta-consultation"
            data-testid="nav-book-call-btn"
          >
            Book Your Free Consultation →
          </a>
          <span className="nav-cta-helper">This is not a medical visit</span>
        </div>
      </div>

      {/* Mobile hamburger trigger - only visible <= 1024px via CSS */}
      <button
        type="button"
        className="navigation-mobile-toggle"
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((v) => !v)}
        data-testid="nav-mobile-toggle"
      >
        {mobileOpen ? <X size={24} strokeWidth={1.6} /> : <Menu size={24} strokeWidth={1.6} />}
      </button>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="navigation-mobile-overlay" onClick={() => setMobileOpen(false)} />
      )}
      <aside
        className={`navigation-mobile-drawer ${mobileOpen ? 'open' : ''}`}
        aria-hidden={!mobileOpen}
        data-testid="nav-mobile-drawer"
      >
        <ul className="navigation-mobile-list">
          <li>
            <Link to="/" className={location.pathname === '/' ? 'active' : ''} data-testid="nav-mobile-home">Home</Link>
          </li>
          <li className="navigation-mobile-services">
            <button
              type="button"
              className={`navigation-mobile-services-trigger ${isServicesActive ? 'active' : ''}`}
              onClick={() => setMobileServicesOpen((v) => !v)}
              aria-expanded={mobileServicesOpen}
              data-testid="nav-mobile-services-trigger"
            >
              <span>Services</span>
              <ChevronDown size={16} className={mobileServicesOpen ? 'open' : ''} />
            </button>
            {mobileServicesOpen && (
              <ul className="navigation-mobile-sublist">
                <li>
                  <Link to="/services" className="navigation-mobile-sublist-all" data-testid="nav-mobile-services-all">
                    View all services
                  </Link>
                </li>
                {SERVICE_LINKS.map((s) => (
                  <li key={s.path}>
                    <Link
                      to={s.path}
                      className={location.pathname === s.path ? 'active' : ''}
                      data-testid={`nav-mobile-service-${s.label.toLowerCase()}`}
                    >
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li>
            <Link to="/pricing" className={location.pathname === '/pricing' ? 'active' : ''}>Pricing</Link>
          </li>
          <li>
            <Link to="/assessment" className={location.pathname === '/assessment' ? 'active' : ''}>Assessment</Link>
          </li>
          <li>
            <Link to="/mystory" className={location.pathname === '/mystory' ? 'active' : ''}>My Story</Link>
          </li>
        </ul>
        <a
          href="/book"
          className="btn-primary navigation-mobile-cta"
          data-testid="nav-mobile-book-btn"
        >
          Book Your Free Consultation →
        </a>
        <p className="navigation-mobile-helper">This is not a medical visit</p>
      </aside>
    </nav>
  );
};

export default Navigation;
